import { BaseModel, type RelationConfig } from './base';

import { Customer } from './customer';
import { Order } from './order';
import { Reservation } from './reservation';
import { Waitlist } from './waitlist';

// Enums

export enum CustomerMessageAttachmentType {
  reservation = 1,
  order = 2,
  customerForm = 3,
  upload = 4,
  customerCourse = 5,
  giftCard = 6,
  customerPass = 7,
  waitlist = 8,
  campaign = 9,
}

export const CustomerMessageAttachmentTypeLabels: Record<CustomerMessageAttachmentType, string> = {
  [CustomerMessageAttachmentType.reservation]: 'general.reservation',
  [CustomerMessageAttachmentType.order]: 'api.models.customerMessage.order',
  [CustomerMessageAttachmentType.customerForm]: 'api.models.customerMessage.customerForm',
  [CustomerMessageAttachmentType.upload]: 'asset.upload',
  [CustomerMessageAttachmentType.customerCourse]: 'api.models.customerMessage.customerCourse',
  [CustomerMessageAttachmentType.giftCard]: 'general.giftCard',
  [CustomerMessageAttachmentType.customerPass]: 'api.models.customerMessage.customerPass',
  [CustomerMessageAttachmentType.waitlist]: 'general.waitlist',
  [CustomerMessageAttachmentType.campaign]: 'general.campaign',
};

export enum CustomerMessageStatus {
  queued = 1,
  sending = 2,
  sent = 3,
  failed = 4,
}

export const CustomerMessageStatusLabels: Record<CustomerMessageStatus, string> = {
  [CustomerMessageStatus.queued]: 'general.queued',
  [CustomerMessageStatus.sending]: 'general.sending',
  [CustomerMessageStatus.sent]: 'general.sent',
  [CustomerMessageStatus.failed]: 'api.models.customerMessage.failed',
};

export enum CustomerMessageSender {
  location = 1,
  customer = 2,
  system = 3,
  employee = 4,
}

export const CustomerMessageSenderLabels: Record<CustomerMessageSender, string> = {
  [CustomerMessageSender.location]: 'general.location',
  [CustomerMessageSender.customer]: 'general.customer',
  [CustomerMessageSender.system]: 'general.system',
  [CustomerMessageSender.employee]: 'general.employee',
};

export enum CustomerMessageChannelStatus {
  doNotSend = 1,
  send = 2,
  sent = 3,
  failed = 4,
  opened = 5,
  origin = 6,
}

export const CustomerMessageChannelStatusLabels: Record<CustomerMessageChannelStatus, string> = {
  [CustomerMessageChannelStatus.doNotSend]: 'api.models.customerMessage.doNotSend',
  [CustomerMessageChannelStatus.send]: 'general.send',
  [CustomerMessageChannelStatus.sent]: 'general.sent',
  [CustomerMessageChannelStatus.failed]: 'api.models.customerMessage.failed',
  [CustomerMessageChannelStatus.opened]: 'general.opened',
  [CustomerMessageChannelStatus.origin]: 'api.models.customerMessage.origin',
};

export enum CustomerMessageType {
  newReservation = 1,
  newReservationConfirmed = 2,
  newReservationRejected = 3,
  newReservationCancelled = 4,
  newReservationReminder = 5,
  reservationMoved = 6,
  reservationCancelled = 7,
  reservationReminder = 8,
  newSale = 9,
  passwordReset = 10,
  receivedCampaign = 11,
  general = 12,
  loginCode = 13,
  newCustomer = 14,
  newOrder = 15,
}

export const CustomerMessageTypeLabels: Record<CustomerMessageType, string> = {
  [CustomerMessageType.newReservation]: 'api.models.customerMessage.newReservation',
  [CustomerMessageType.newReservationConfirmed]: 'api.models.customerMessage.newReservationConfirmed',
  [CustomerMessageType.newReservationRejected]: 'api.models.customerMessage.newReservationRejected',
  [CustomerMessageType.newReservationCancelled]: 'api.models.customerMessage.newReservationCancelled',
  [CustomerMessageType.newReservationReminder]: 'api.models.customerMessage.newReservationReminder',
  [CustomerMessageType.reservationMoved]: 'api.models.customerMessage.reservationMoved',
  [CustomerMessageType.reservationCancelled]: 'api.models.customerMessage.reservationCancelled',
  [CustomerMessageType.reservationReminder]: 'api.models.customerMessage.reservationReminder',
  [CustomerMessageType.newSale]: 'order.new',
  [CustomerMessageType.passwordReset]: 'api.models.customerMessage.passwordReset',
  [CustomerMessageType.receivedCampaign]: 'api.models.customerMessage.receivedCampaign',
  [CustomerMessageType.general]: 'api.models.customerMessage.general',
  [CustomerMessageType.loginCode]: 'api.models.customerMessage.loginCode',
  [CustomerMessageType.newCustomer]: 'customer.new',
  [CustomerMessageType.newOrder]: 'api.models.customerMessage.newOrder',
};

// Helper Classes

export class CustomerMessageData extends BaseModel {
  customer!: Customer | null;
  order!: Order | null;
  reservation!: Reservation | null;
  waitlist!: Waitlist | null;

  static override relations(): RelationConfig {
    return {
      customer: { type: Customer },
      order: { type: Order },
      reservation: { type: Reservation },
      waitlist: { type: Waitlist },
    };
  }
}

export class CustomerMessageAttachment extends BaseModel {
  type!: CustomerMessageAttachmentType;

  get isTypeReservation(): boolean { return this.type === CustomerMessageAttachmentType.reservation; }
  get isTypeOrder(): boolean { return this.type === CustomerMessageAttachmentType.order; }
  get isTypeCustomerForm(): boolean { return this.type === CustomerMessageAttachmentType.customerForm; }
  get isTypeUpload(): boolean { return this.type === CustomerMessageAttachmentType.upload; }
  get isTypeCustomerCourse(): boolean { return this.type === CustomerMessageAttachmentType.customerCourse; }
  get isTypeGiftCard(): boolean { return this.type === CustomerMessageAttachmentType.giftCard; }
  get isTypeCustomerPass(): boolean { return this.type === CustomerMessageAttachmentType.customerPass; }
  get isTypeWaitlist(): boolean { return this.type === CustomerMessageAttachmentType.waitlist; }
  get isTypeCampaign(): boolean { return this.type === CustomerMessageAttachmentType.campaign; }
}

// Main Model

export class CustomerMessage extends BaseModel {
  idCustomerMessage!: number;
  subject!: string;
  body!: string;
  status!: CustomerMessageStatus;
  sender!: CustomerMessageSender;
  type!: CustomerMessageType;
  channelEmail!: CustomerMessageChannelStatus;
  channelSms!: CustomerMessageChannelStatus;
  channelApp!: CustomerMessageChannelStatus;
  customer!: Customer | null;
  data!: CustomerMessageData | null;
  attachments!: CustomerMessageAttachment[];

  static override relations(): RelationConfig {
    return {
      customer: { type: Customer },
      data: { type: CustomerMessageData },
      attachments: { type: CustomerMessageAttachment },
    };
  }

  override objectID(): string | null {
    return this.idCustomerMessage?.toString() ?? null;
  }

  // Sender getters
  get isSenderLocation(): boolean { return this.sender === CustomerMessageSender.location; }
  get isSenderCustomer(): boolean { return this.sender === CustomerMessageSender.customer; }
  get isSenderSystem(): boolean { return this.sender === CustomerMessageSender.system; }
  get isSenderEmployee(): boolean { return this.sender === CustomerMessageSender.employee; }

  // Email channel getters
  get isChannelEmailDoNotSend(): boolean { return this.channelEmail === CustomerMessageChannelStatus.doNotSend; }
  get isChannelEmailSend(): boolean { return this.channelEmail === CustomerMessageChannelStatus.send; }
  get isChannelEmailSent(): boolean { return this.channelEmail === CustomerMessageChannelStatus.sent; }
  get isChannelEmailFailed(): boolean { return this.channelEmail === CustomerMessageChannelStatus.failed; }
  get isChannelEmailOpened(): boolean { return this.channelEmail === CustomerMessageChannelStatus.opened; }
  get isChannelEmailOrigin(): boolean { return this.channelEmail === CustomerMessageChannelStatus.origin; }

  // SMS channel getters
  get isChannelSMSDoNotSend(): boolean { return this.channelSms === CustomerMessageChannelStatus.doNotSend; }
  get isChannelSMSSend(): boolean { return this.channelSms === CustomerMessageChannelStatus.send; }
  get isChannelSMSSent(): boolean { return this.channelSms === CustomerMessageChannelStatus.sent; }
  get isChannelSMSFailed(): boolean { return this.channelSms === CustomerMessageChannelStatus.failed; }
  get isChannelSMSOpened(): boolean { return this.channelSms === CustomerMessageChannelStatus.opened; }
  get isChannelSMSOrigin(): boolean { return this.channelSms === CustomerMessageChannelStatus.origin; }

  // App channel getters
  get isChannelAppDoNotSend(): boolean { return this.channelApp === CustomerMessageChannelStatus.doNotSend; }
  get isChannelAppSend(): boolean { return this.channelApp === CustomerMessageChannelStatus.send; }
  get isChannelAppSent(): boolean { return this.channelApp === CustomerMessageChannelStatus.sent; }
  get isChannelAppFailed(): boolean { return this.channelApp === CustomerMessageChannelStatus.failed; }
  get isChannelAppOpened(): boolean { return this.channelApp === CustomerMessageChannelStatus.opened; }
  get isChannelAppOrigin(): boolean { return this.channelApp === CustomerMessageChannelStatus.origin; }

  // Type getters
  get isTypeNewReservation(): boolean { return this.type === CustomerMessageType.newReservation; }
  get isTypeNewReservationConfirmed(): boolean { return this.type === CustomerMessageType.newReservationConfirmed; }
  get isTypeNewReservationRejected(): boolean { return this.type === CustomerMessageType.newReservationRejected; }
  get isTypeNewReservationCancelled(): boolean { return this.type === CustomerMessageType.newReservationCancelled; }
  get isTypeNewReservationReminder(): boolean { return this.type === CustomerMessageType.newReservationReminder; }
  get isTypeReservationMoved(): boolean { return this.type === CustomerMessageType.reservationMoved; }
  get isTypeReservationCancelled(): boolean { return this.type === CustomerMessageType.reservationCancelled; }
  get isTypeReservationReminder(): boolean { return this.type === CustomerMessageType.reservationReminder; }
  get isTypeNewSale(): boolean { return this.type === CustomerMessageType.newSale; }
  get isTypePasswordReset(): boolean { return this.type === CustomerMessageType.passwordReset; }
  get isTypeReceivedCampaign(): boolean { return this.type === CustomerMessageType.receivedCampaign; }
  get isTypeGeneral(): boolean { return this.type === CustomerMessageType.general; }
  get isTypeLoginCode(): boolean { return this.type === CustomerMessageType.loginCode; }
  get isTypeNewCustomer(): boolean { return this.type === CustomerMessageType.newCustomer; }
  get isTypeNewOrder(): boolean { return this.type === CustomerMessageType.newOrder; }

  // Status getters
  get isStatusQueued(): boolean { return this.status === CustomerMessageStatus.queued; }
  get isStatusSending(): boolean { return this.status === CustomerMessageStatus.sending; }
  get isStatusSent(): boolean { return this.status === CustomerMessageStatus.sent; }
  get isStatusFailed(): boolean { return this.status === CustomerMessageStatus.failed; }
}
